import { useSEO } from "@/hooks/useSEO";
import { SeoPageLayout } from "@/components/seo/SeoPageLayout";
import { SeoSection, SeoSubSection, SeoList } from "@/components/seo/SeoContent";
import { Link } from "react-router-dom";
import { AccompanimentInlineDialog } from "@/components/seo/AccompanimentInlineDialog";

const SITE = "https://www.regard-de-nature.fr";

const ParentaliteBurnout = () => {
  useSEO({
    title: "Naturopathie parentalité & burn-out parental – Regard de Nature",
    description:
      "Soutien naturopathique pour les parents épuisés : charge mentale, fatigue, stress, burn-out parental. Accompagnement personnalisé par Laura Elie.",
    canonical: `${SITE}/parentalite-burn-out-parental`,
  });

  return (
    <SeoPageLayout
      h1="Parentalité & burn-out parental"
      intro="Soutenir les parents face à la charge mentale, à l'épuisement parental et aux défis du quotidien familial."
    >
      <SeoSection title="Burn-out parental, charge mentale et épuisement des parents">
        <p>Vous vous sentez constamment fatigué, dépassé ou sous pression ?</p>
        <p>
          Le burn-out parental touche de nombreux parents, en particulier lorsqu'ils accompagnent un enfant
          atypique, un enfant TDAH, un enfant hypersensible ou un enfant présentant des troubles DYS.
        </p>
        <p>
          La charge mentale, les rendez-vous médicaux, les difficultés scolaires, les troubles du sommeil de
          l'enfant ou les inquiétudes permanentes peuvent progressivement conduire à un épuisement physique,
          émotionnel et mental.
        </p>
        <SeoSubSection title="Les signes les plus fréquents">
          <SeoList
            items={[
              "Fatigue chronique",
              "Manque d'énergie",
              "Troubles du sommeil",
              "Irritabilité",
              "Stress chronique",
              "Anxiété",
              "Surcharge mentale",
              "Perte de motivation",
              "Sentiment d'épuisement parental",
            ]}
          />
        </SeoSubSection>
        <p>
          L'accompagnement naturopathique permet de travailler sur les piliers essentiels de votre équilibre :
          sommeil, gestion du stress, récupération nerveuse, alimentation, vitalité et hygiène de vie.
        </p>
      </SeoSection>

      <SeoSection title="Retrouver de l'énergie et prévenir le burn-out parental">
        <p>
          Prendre soin de soi n'est pas un luxe lorsque l'on est parent. C'est souvent une nécessité pour retrouver
          un équilibre durable et préserver sa santé physique, émotionnelle et mentale.
        </p>
        <p>
          Lors du bilan naturopathique adulte, nous prenons le temps d'explorer les causes de votre fatigue, de
          votre stress ou de votre surcharge mentale afin de mettre en place des solutions adaptées à votre
          situation et à votre rythme de vie.
        </p>
        <p>
          👉{" "}
          <AccompanimentInlineDialog
            slug="bilan-naturopathique-adultes"
            trigger={
              <button className="text-primary underline hover:text-secondary text-left">
                Réserver un bilan naturopathique adulte
              </button>
            }
          />
        </p>
      </SeoSection>

      <SeoSection title="Un accompagnement global pour les familles d'enfants atypiques">
        <p>
          Lorsque l'épuisement parental s'ajoute aux défis liés au TDAH, à l'hypersensibilité ou aux troubles DYS
          de l'enfant, il peut être bénéfique d'accompagner l'ensemble de la famille.
        </p>
        <SeoSubSection title="Le Parcours Famille Courageuse se déroule en trois rendez-vous">
          <SeoList
            items={[
              "Un premier rendez-vous consacré au bilan naturopathique de l'enfant",
              "Un deuxième rendez-vous dédié au bilan naturopathique du parent",
              "Un troisième rendez-vous de suivi familial",
            ]}
          />
        </SeoSubSection>
        <p>
          À l'issue des deux bilans, chacun reçoit un Programme d'Hygiène de Vie personnalisé, adapté à ses besoins
          et à son quotidien.
        </p>
        <p>
          Cet accompagnement permet de travailler sur le bien-être de chacun afin de retrouver davantage de
          sérénité, d'énergie et d'harmonie au sein de la famille.
        </p>
        <p>
          👉{" "}
          <AccompanimentInlineDialog
            slug="parcours-famille-courageuse"
            trigger={
              <button className="text-primary underline hover:text-secondary text-left">
                Découvrir le Parcours Famille Courageuse
              </button>
            }
          />
        </p>
      </SeoSection>

      <SeoSection title="💻 Des consultations adaptées à votre quotidien">
        <p>
          Lorsque l'on se sent déjà fatigué, débordé ou en surcharge mentale, organiser un déplacement
          supplémentaire n'est pas toujours simple.
        </p>
        <p>
          Les consultations en visioconférence permettent de bénéficier d'un accompagnement personnalisé depuis
          chez vous, partout en France, dans un cadre confortable et rassurant.
        </p>
      </SeoSection>
    </SeoPageLayout>
  );
};

export default ParentaliteBurnout;
